import React, { useRef, useState } from 'react';
import './worlds.css';

/**
 * ZenGarden — a sand tray you rake by dragging. Press and drag across the sand
 * (mouse, touch or pen) to draw a slow raked line; a handful of lines are kept,
 * older ones fading, with a couple of still stones to flow around. A drag-based,
 * meditative activity — no targets to hit, nothing to finish. Coordinates are
 * stored as percentages so the strokes scale with the tray.
 */
interface Stroke { id: number; pts: string; }

export const ZenGarden: React.FC = () => {
  const boxRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const drawing = useRef(false);
  const ptsRef = useRef<string[]>([]);
  const raf = useRef(0);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [current, setCurrent] = useState('');

  const toPct = (clientX: number, clientY: number): string | null => {
    const el = boxRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const x = ((clientX - r.left) / r.width) * 100;
    const y = ((clientY - r.top) / r.height) * 100;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };

  const start = (e: React.PointerEvent<HTMLDivElement>) => {
    drawing.current = true;
    ptsRef.current = [];
    const p = toPct(e.clientX, e.clientY);
    if (p) ptsRef.current.push(p);
    setCurrent(ptsRef.current.join(' '));
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const move = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drawing.current) return;
    const p = toPct(e.clientX, e.clientY);
    if (!p) return;
    ptsRef.current.push(p);
    if (!raf.current) {
      raf.current = requestAnimationFrame(() => {
        raf.current = 0;
        setCurrent(ptsRef.current.join(' '));
      });
    }
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (raf.current) { cancelAnimationFrame(raf.current); raf.current = 0; }
    if (ptsRef.current.length > 1) {
      const id = seq.current++;
      const pts = ptsRef.current.join(' ');
      setStrokes((s) => [...s.slice(-6), { id, pts }]);
    }
    ptsRef.current = [];
    setCurrent('');
  };

  return (
    <div
      ref={boxRef}
      className="mm-world mm-world--zen"
      role="img"
      aria-label="Zen sand garden — drag to rake the sand"
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      <svg className="mm-zen__svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {strokes.map((s, i) => (
          <g key={s.id} style={{ opacity: 0.4 + (i / Math.max(1, strokes.length)) * 0.5 }}>
            <polyline className="mm-zen__groove" points={s.pts} />
            <polyline className="mm-zen__tine" points={s.pts} />
          </g>
        ))}
        {current && (
          <g>
            <polyline className="mm-zen__groove" points={current} />
            <polyline className="mm-zen__tine" points={current} />
          </g>
        )}
      </svg>

      <span className="mm-zen__stone mm-zen__stone--1" aria-hidden="true" />
      <span className="mm-zen__stone mm-zen__stone--2" aria-hidden="true" />

      <p className="mm-world__hint">Drag to rake the sand</p>
    </div>
  );
};

export default ZenGarden;
